import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/inspire.css';
import '../styles/inspire-home.css';
import '../styles/service-page.css';

const InHomeTherapy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const goToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const helpItems = [
    "Children who feel more comfortable and regulated in their own space",
    "Families who want therapy integrated into their daily environment",
    "Kids with school avoidance, social anxiety, or sensory sensitivities",
    "Situations where observing the home environment adds clinical value",
    "Families with scheduling constraints or transportation challenges",
    "Parent coaching and behavioral support in real-time, real-life settings"
  ];

  const fitItems = [
    "Your child does better in familiar surroundings than in a clinical office",
    "You want therapy to feel more natural and less intimidating for your child",
    "You'd like the therapist to see your child's behavior in context — at home",
    "You want hands-on parent coaching during real-life moments",
    "Your family's schedule makes office visits difficult",
    "You live in the Lake Nona area and want a more convenient option"
  ];

  return (
    <div className="therapy-site" data-testid="in-home-therapy-page">
      <Helmet>
        <title>In-Home Therapy in Lake Nona | Inspire Therapy Collective - Orlando</title>
        <meta name="description" content="In-home therapy services for children and families in Lake Nona, Orlando. Therapy in the comfort of your home for more natural, effective support." />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="service-hero">
        <div 
          className="service-hero-overlay" 
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1633505899118-4ca6bd143043?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxjb3p5JTIwaG9tZSUyMGxpdmluZyUyMHJvb20lMjB3YXJtJTIwc3VubGlnaHQlMjBib2hvfGVufDB8fHx8MTc3MjA1NzA5NXww&ixlib=rb-4.1.0&q=85')" }}
        />
        <div className="service-hero-gradient" />
        <div className="service-hero-content fade-in-up">
          <h1 className="gradient-text" data-testid="inhome-page-title">In-Home Therapy</h1>
          <p className="service-tagline">
            Therapy in the comfort of your home — where your child feels safest and support feels most natural.
          </p>
          <span className="service-location-tag">Available in Lake Nona, Orlando FL</span>
        </div>
      </section>

      {/* Intro */}
      <section className="service-intro">
        <div className="container">
          <div className="service-intro-content fade-in-up">
            <p>
              Some children do their best work outside the therapy office — in the environment where they feel most themselves. In-home therapy brings the support directly to your family, removing barriers and meeting your child exactly where they are — literally.
            </p>
            <p>
              With years of experience providing in-home ABA therapy and family-based interventions, I understand the unique value of working within the home environment. I can observe real-life dynamics, provide parent coaching in the moment, and help build strategies that fit seamlessly into your family's daily routine.
            </p>
            <p>
              In-home sessions follow the same evidence-based, developmentally attuned approach I use in all my work — just in a setting that often feels more natural and less intimidating for children and families.
            </p>
          </div>
        </div>
      </section>

      {/* What I Help With */}
      <section className="service-help-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text" data-testid="inhome-help-heading">Who In-Home Therapy Is For</h2>
          <div className="service-help-grid">
            {helpItems.map((item, index) => (
              <div key={index} className="service-help-item fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="service-help-icon">
                  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id={`helpIconHome${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#F9C8A8" />
                        <stop offset="50%" stopColor="#E6A9C0" />
                        <stop offset="100%" stopColor="#C4A8E2" />
                      </linearGradient>
                    </defs>
                    <circle cx="14" cy="14" r="10" stroke={`url(#helpIconHome${index})`} strokeWidth="1.5" fill="none" opacity="0.3" />
                    <path d="M10 14L13 17L18 11" stroke={`url(#helpIconHome${index})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* My Approach */}
      <section className="service-approach">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">How It Works</h2>
          <svg className="service-sun-divider fade-in" viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="sunDividerHome" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F9C8A8" />
                <stop offset="50%" stopColor="#E6A9C0" />
                <stop offset="100%" stopColor="#C4A8E2" />
              </linearGradient>
            </defs>
            <line x1="10" y1="45" x2="70" y2="45" stroke="url(#sunDividerHome)" strokeWidth="1.5" opacity="0.35" />
            <path d="M 40 45 A 15 15 0 0 1 25 45 L 40 45 Z" fill="url(#sunDividerHome)" opacity="0.75" />
          </svg>
          <div className="service-approach-content fade-in-up">
            <p>
              In-home therapy sessions are currently available for families in the Lake Nona area of Orlando, FL. Sessions are scheduled at a time that works for your family and take place in the comfort of your home.
            </p>
            <p>
              I bring the same warmth, structure, and evidence-based approach to every in-home session. Whether we're working on emotional regulation through play, practicing social skills in real-time, or providing parent coaching during everyday routines — the goal is always to build skills that stick.
            </p>
            <p>
              In-home therapy can be combined with office-based sessions or telehealth for a flexible, blended approach that meets your family's unique needs.
            </p>
          </div>
        </div>
      </section>

      {/* Good Fit */}
      <section className="service-fit-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">This Might Be a Good Fit If...</h2>
          <div className="service-fit-list">
            {fitItems.map((item, index) => (
              <div key={index} className="service-fit-item fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="fit-bullet" />
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="service-cta">
        <div className="container">
          <div className="service-cta-content fade-in-up">
            <h2 className="heading-2 font-serif gradient-text">Bring Therapy Home</h2>
            <p>
              If you're curious about whether in-home therapy is the right fit for your family, I'd love to talk. Reach out and we'll figure it out together.
            </p>
            <div className="hero-cta-group" style={{ justifyContent: 'center' }}>
              <button onClick={goToContact} className="btn btn-primary" data-testid="inhome-cta-primary">
                Book a Free 15-Min Consult
              </button>
              <button onClick={() => navigate('/', { state: { scrollTo: 'insurance' } })} className="btn btn-secondary-outline" data-testid="inhome-cta-secondary">
                Check Insurance & Book
              </button>
            </div>
            <div className="service-cta-details">
              <a href="tel:4072019895" className="cta-phone">(407) 201-9895</a>
              <span className="cta-location">Available in Lake Nona, Orlando FL</span>
            </div>
            <button onClick={() => navigate('/', { state: { scrollTo: 'services' } })} className="explore-all-link" data-testid="explore-all-services-link">
              &larr; Explore All Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InHomeTherapy;
